/**
 * Seasonal Pricing utilities.
 *
 * Apply price multipliers for specific months/date ranges.
 * Different from dynamicPricing (demand-based) and priceRules (custom rules).
 * This is simpler — fixed multipliers per season.
 *
 * Config in listing.publicData.seasonalPricing = [
 *   { name: 'Summer', months: [6, 7, 8], multiplier: 1.5 },
 *   { name: 'Winter peak', months: [12, 1, 2], multiplier: 1.3 },
 *   { name: 'Off-season', months: [3, 4, 5, 9, 10, 11], multiplier: 0.8 },
 * ]
 *
 * Months: 1-12 (January = 1)
 */

export const DEFAULT_MULTIPLIER = 1.0;

/**
 * Get seasonal pricing config from listing.
 * @param {Object|null} listing
 * @returns {Object[]}
 */
export const getSeasonalPricing = (listing) =>
  listing?.attributes?.publicData?.seasonalPricing || [];

/**
 * Get active season for a date.
 * @param {Object[]} seasons
 * @param {Date} [date]
 * @returns {Object|null}
 */
export const getActiveSeason = (seasons, date = new Date()) => {
  if (!Array.isArray(seasons)) return null;
  const month = date.getMonth() + 1; // 1-12
  return seasons.find(s => Array.isArray(s.months) && s.months.includes(month)) || null;
};

/**
 * Get price multiplier for a date.
 * @param {Object[]} seasons
 * @param {Date} [date]
 * @returns {number}
 */
export const getSeasonMultiplier = (seasons, date = new Date()) => {
  const season = getActiveSeason(seasons, date);
  return season?.multiplier ?? DEFAULT_MULTIPLIER;
};

/**
 * Apply seasonal multiplier to price.
 * @param {number} basePriceCents
 * @param {Object[]} seasons
 * @param {Date} [date]
 * @returns {number}
 */
export const applySeasonalPrice = (basePriceCents, seasons, date = new Date()) => {
  const multiplier = getSeasonMultiplier(seasons, date);
  return Math.round(basePriceCents * multiplier);
};

/**
 * Validate seasonal pricing config.
 * @param {Object[]} seasons
 * @returns {string|null}
 */
export const validateSeasonalPricing = (seasons) => {
  if (!Array.isArray(seasons)) return 'Must be an array';
  for (const season of seasons) {
    if (!season.name) return 'Each season needs a name';
    if (!Array.isArray(season.months) || season.months.length === 0) return `Season "${season.name}" needs at least one month`;
    if (season.months.some(m => m < 1 || m > 12)) return `Season "${season.name}" has invalid month (must be 1-12)`;
    if (typeof season.multiplier !== 'number' || season.multiplier <= 0) return `Season "${season.name}" multiplier must be > 0`;
  }
  return null;
};
